import { env } from "@/lib/env";
import {
  GetListingByIdResponse,
  GetUserListingsResponse,
  ListingChanges,
  ListingDeleteState,
  MyListingsState,
  UserListingCard,
} from "./types";
import { ApiError } from "next/dist/server/api-utils";
import { toast } from "react-toastify";
import { create } from "zustand";
import {
  AutocompleteSuggestion,
  ListingTypeEnum,
  PurposeEnum,
  TypeEnum,
} from "@/types/types";
import { ChangeEvent } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { apiFetch } from "@/lib/apiFetch";

const LISTINGSENDPOINTURL = env.NEXT_LISTINGS_API_URL;
const PAYMENTSENDPOINTURL = env.NEXT_PAYMENTS_API_URL;
const SEARCHENDPOINTURL = env.NEXT_SEARCH_API_URL;
let debounceTimeout: ReturnType<typeof setTimeout>;

const myListingsSyncChannel =
  typeof window !== "undefined"
    ? new BroadcastChannel("my-listings-mutations")
    : null;

type SyncEvent =
  | { type: "DELETED"; listingId: string }
  | {
      type: "UPDATED";
      listingId: string;
      partialData: Partial<UserListingCard>;
    };

interface ListingStore {
  state: MyListingsState;
  settingsModalListing: UserListingCard | null;
  pinnedListing: UserListingCard | null;
  setPinnedListingNull: () => void;
  setSettingsModalListing: (newVal: UserListingCard | null) => void;
  isRefetchingListingCardLoading: string;
  listingsLocale: string;
  applySyncEvent: (event: SyncEvent) => void;
  initFirstPage: (data: GetUserListingsResponse, locale: string) => void;
  fetchPage: (
    locale: string,
    pageNumber: number,
    sessionId: string | null,
  ) => Promise<void>;
  activateListing: (
    listingId: string,
    sessionId: string | null,
  ) => Promise<boolean>;
  deactivateListing: (
    listingId: string,
    sessionId: string | null,
  ) => Promise<boolean>;
  changePlan: (
    listingId: string,
    newPlan: ListingTypeEnum,
    sessionId: string | null,
  ) => Promise<boolean>;
  cancelPlan: (listingId: string, sessionId: string | null) => Promise<boolean>;
  changeListingTypeNoPlan: (
    newType: ListingTypeEnum,
    listingId: string,
    sessionId: string | null,
  ) => Promise<void>;
  saveListingSettings: (
    listingId: string,
    sessionId: string | null,
    changes: ListingChanges,
  ) => Promise<void>;
  deleteListing: (locale: string, sessionId: string | null) => Promise<void>;
  openDeleteModal: (listingId: string) => void;
  closeDeleteModal: () => void;
  setSoldOnDoomum: (value: boolean) => void;
  deleteState: ListingDeleteState;
  refetchEditedListing: (
    listingId: string,
    sessionId: string | null,
    locale: string,
  ) => Promise<void>;
  getListingById: (
    listingId: string,
    locale: string,
  ) => Promise<UserListingCard | null>;
  text: string;
  type: TypeEnum;
  purpose: PurposeEnum;
  onTextChange: (
    e: ChangeEvent<HTMLInputElement>,
    locale: string,
    sessionId: string | null,
  ) => void;
  refetchState: (locale: string, sessionId: string | null) => Promise<void>;
  setType: (type: TypeEnum) => void;
  setPurpose: (purpose: PurposeEnum) => void;
}

export const useMyListingsStore = create<ListingStore>((set, get) => ({
  state: {
    listings: [],
    total_count: 0,
    page_number: 1,
    isLoaded: false,
    pageLoading: true,
    total_number_of_pages: 1,
    fetched_at: 0,
    current_suggestion: {
      type: "",
      value: "",
      page_number: 1,
    },
  },
  settingsModalListing: null,
  pinnedListing: null,
  setPinnedListingNull: () => {
    set({ pinnedListing: null });
  },
  setSettingsModalListing: (newVal) => {
    set({ settingsModalListing: newVal });
  },
  isRefetchingListingCardLoading: "",
  listingsLocale: "en",
  applySyncEvent: (event: SyncEvent) => {
    set((currentState) => {
      if (event.type === "DELETED") {
        return {
          state: {
            ...currentState.state,
            listings: currentState.state.listings.filter(
              (l) => l.listing_id !== event.listingId,
            ),
            total_count: Math.max(0, currentState.state.total_count - 1),
          },
          // Optional: close modal if the deleted listing was open
          settingsModalListing:
            currentState.settingsModalListing?.listing_id === event.listingId
              ? null
              : currentState.settingsModalListing,
        };
      }
      if (event.type === "UPDATED") {
        return {
          state: {
            ...currentState.state,
            listings: currentState.state.listings.map((l) =>
              l.listing_id === event.listingId
                ? { ...l, ...event.partialData }
                : l,
            ),
          },
          settingsModalListing:
            currentState.settingsModalListing?.listing_id === event.listingId
              ? { ...currentState.settingsModalListing, ...event.partialData }
              : currentState.settingsModalListing,
          pinnedListing:
            currentState.pinnedListing?.listing_id === event.listingId
              ? { ...currentState.pinnedListing, ...event.partialData }
              : currentState.pinnedListing,
        };
      }
      return currentState;
    });
  },
  initFirstPage: async (data, locale) => {
    const pinned = get().pinnedListing;

    const mergedListings = pinned
      ? [
          pinned,
          ...data.listings.filter((l) => l.listing_id !== pinned.listing_id),
        ]
      : data.listings;
    set((currentState) => ({
      ...currentState,
      state: { ...currentState.state, pageLoading: true },
    }));
    set((currentState) => ({
      ...currentState,
      state: {
        ...currentState.state,
        listings: mergedListings,
        pageLoading: false,
        isLoading: false,
        isLoaded: true,
        page_number: data.page_number,
        total_count: data.total_count,
        total_number_of_pages: data.number_of_pages,
        current_suggestion: data.current_suggestion,
      },
      listingsLocale: locale,
      text: "",
      type: TypeEnum.DEFAULT,
      purpose: PurposeEnum.DEFAULT,
    }));
  },
  fetchPage: async (locale, pageNumber, sessionId) => {
    const isAlreadyLoading = get().state.pageLoading;
    if (isAlreadyLoading) {
      return;
    }
    if (!sessionId) {
      toast("Please reauthenticate before performing this action.", {
        type: "error",
      });
      return;
    }
    try {
      set((state) => ({
        ...state,
        pageLoading: true,
      }));
      const type = get().type;
      const purpose = get().purpose;
      const currentSuggestion = get().state.current_suggestion;
      const text = get().text;
      const response = await fetch(
        `${SEARCHENDPOINTURL}/getUserListings?text=${text}&locale=${locale}&current_suggestion_type=${currentSuggestion.type}&current_suggestion_value=${currentSuggestion.value}&current_suggestion_page=${pageNumber}&type=${type}&purpose=${purpose}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionId,
          },
        },
      );

      if (response.ok) {
        const apiResponse = (await response.json()) as GetUserListingsResponse;
        const pinned = get().pinnedListing;

        const mergedListings = pinned
          ? [
              pinned,
              ...apiResponse.listings.filter(
                (l) => l.listing_id !== pinned.listing_id,
              ),
            ]
          : apiResponse.listings;
        set((currentState) => ({
          ...currentState,
          state: {
            ...currentState.state,
            listings: mergedListings,
            pageLoading: false,
            page_number: apiResponse.page_number,
            total_count: apiResponse.total_count,
            total_number_of_pages: apiResponse.number_of_pages,
            current_suggestion: apiResponse.current_suggestion,
          },
        }));
      } else {
        const apiErr = (await response.json()) as ApiError;
        throw new Error(apiErr.message);
      }
    } catch (err) {
      set((state) => ({
        ...state,
        pageLoading: false,
      }));
      const errorMessage =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      toast(
        errorMessage === "Failed to fetch"
          ? "Unable to get user listings."
          : errorMessage,
        {
          type: "error",
        },
      );
      return;
    }
  },
  activateListing: async (listingId, sessionId) => {
    if (!sessionId) {
      toast("Please reauthenticate before performing this action.", {
        type: "error",
      });
      return false;
    }
    const prevListing = get().state.listings.find(
      (l) => l.listing_id === listingId,
    );
    set((currentState) => ({
      state: {
        ...currentState.state,
        listings: currentState.state.listings.map((listing) =>
          listing.listing_id === listingId
            ? {
                ...listing,
                status: "active",
              }
            : listing,
        ),
      },
    }));
    try {
      const response = await fetch(`${LISTINGSENDPOINTURL}/activateListing`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionId,
          origin: "http://doomum.ro:3000/",
        },
        body: JSON.stringify({ listing_id: listingId }),
      });

      if (!response.ok) {
        const apiErr = (await response.json()) as ApiError;
        throw new Error(apiErr.message);
      }
      const responseExpiresAt: { expires_at: number } = await response.json();
      set((currentState) => ({
        state: {
          ...currentState.state,
          listings: currentState.state.listings.map((listing) =>
            listing.listing_id === listingId
              ? {
                  ...listing,
                }
              : listing,
          ),
        },
      }));
      if (myListingsSyncChannel) {
        myListingsSyncChannel.postMessage({
          type: "UPDATED",
          listingId,
          partialData: { status: "active" },
        });
      }
      return true;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      toast(
        errorMessage === "Failed to fetch"
          ? "Unable to update your listing."
          : errorMessage,
        {
          type: "error",
        },
      );
      if (prevListing) {
        set((currentState) => ({
          state: {
            ...currentState.state,
            listings: currentState.state.listings.map((listing) =>
              listing.listing_id === listingId ? prevListing : listing,
            ),
          },
        }));
      }
      return false;
    }
  },
  deactivateListing: async (listingId, sessionId) => {
    if (!sessionId) {
      toast("Please reauthenticate before performing this action.", {
        type: "error",
      });
      return false;
    }
    const prevListing = get().state.listings.find(
      (l) => l.listing_id === listingId,
    );
    set((currentState) => ({
      state: {
        ...currentState.state,
        listings: currentState.state.listings.map((listing) =>
          listing.listing_id === listingId
            ? {
                ...listing,
                status: "inactive",
              }
            : listing,
        ),
      },
    }));
    try {
      const response = await fetch(`${LISTINGSENDPOINTURL}/deactivateListing`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionId,
          origin: "http://doomum.ro:3000/",
        },
        body: JSON.stringify({ listing_id: listingId }),
      });

      if (!response.ok) {
        const apiErr = (await response.json()) as ApiError;
        throw new Error(apiErr.message);
      }
      if (myListingsSyncChannel) {
        myListingsSyncChannel.postMessage({
          type: "UPDATED",
          listingId,
          partialData: { status: "inactive" },
        });
      }
      return true;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      toast(
        errorMessage === "Failed to fetch"
          ? "Unable to update your listing."
          : errorMessage,
        {
          type: "error",
        },
      );
      if (prevListing) {
        set((currentState) => ({
          state: {
            ...currentState.state,
            listings: currentState.state.listings.map((listing) =>
              listing.listing_id === listingId ? prevListing : listing,
            ),
          },
        }));
      }
      return false;
    }
  },
  changePlan: async (listingId, newPlan, sessionId) => {
    const prevListing = get().state.listings.find(
      (l) => l.listing_id === listingId,
    );
    if (!prevListing) {
      toast("The listing you are trying to update does not exist.", {
        type: "error",
      });
      return false;
    }

    // 🚀 FIX: Removed Optimistic Update! We wait for Stripe/Backend confirmation first.

    try {
      const response = await apiFetch(`/api/payments/listings/change-plan`, {
        method: "POST",
        body: JSON.stringify({
          listing_id: listingId,
          new_product: newPlan,
        }),
      });

      if (!response.ok) {
        const apiErr = (await response.json()) as ApiError;
        throw new Error(apiErr.message);
      }

      // We expect this exact payload from your Go backend
      const data = (await response.json()) as {
        success: boolean;
        message: string;
        action_taken: "UPGRADE_IMMEDIATE" | "DOWNGRADE_SCHEDULED";
        client_secret?: string;
        requires_action?: boolean;
      };

      // --- 1. Handle Stripe 3D Secure Validation ---
      if (data.requires_action && data.client_secret) {
        const stripe = await loadStripe(env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY);

        const { error, paymentIntent } = await stripe!.confirmCardPayment(
          data.client_secret,
        );

        if (error) {
          toast(error.message || "Payment verification failed.", {
            type: "error",
          });
          return false; // Safely exit. No rollback needed because we didn't optimistically update!
        }

        if (paymentIntent?.status !== "succeeded") {
          return false;
        }
      }

      // --- 2. Safely Update UI State ---
      // ONLY update the listing_type badge if it was an immediate upgrade.
      // If it's a scheduled downgrade, we leave the state alone (they keep their high-tier badge until next bill)
      if (data.action_taken === "UPGRADE_IMMEDIATE") {
        set((currentState) => ({
          state: {
            ...currentState.state,
            listings: currentState.state.listings.map((listing) =>
              listing.listing_id === listingId
                ? { ...listing, listing_type: newPlan }
                : listing,
            ),
          },
          settingsModalListing:
            currentState.settingsModalListing?.listing_id === listingId
              ? { ...currentState.settingsModalListing, listing_type: newPlan }
              : currentState.settingsModalListing,
        }));
        if (myListingsSyncChannel) {
          myListingsSyncChannel.postMessage({
            type: "UPDATED",
            listingId,
            partialData: { listing_type: newPlan },
          });
        }
      }

      return true;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      toast(
        errorMessage === "Failed to fetch"
          ? "Unable to update your listing plan."
          : errorMessage,
        { type: "error" },
      );
      // No rollback needed here either!
      return false;
    }
  },
  cancelPlan: async (listingId, sessionId) => {
    try {
      const response = await apiFetch(`/api/payments/listings/cancel`, {
        method: "POST",
        body: JSON.stringify({
          listing_id: listingId,
        }),
      });

      if (!response.ok) {
        const apiErr = (await response.json()) as ApiError;
        throw new Error(apiErr.message);
      }

      return true;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      toast(
        errorMessage === "Failed to fetch"
          ? "Unable to cancel your listing plan."
          : errorMessage,
        {
          type: "error",
        },
      );
      return false;
    }
  },
  changeListingTypeNoPlan: async (newType, listingId, sessionId) => {
    if (!sessionId) {
      toast("Please reauthenticate before performing this action.", {
        type: "error",
      });
      return;
    }
    const prevListing = get().state.listings.find(
      (l) => l.listing_id === listingId,
    );
    if (prevListing === undefined) {
      toast("The listing you are trying to update does not exist.", {
        type: "error",
      });
      return;
    }

    // Optimistically update the listing_type
    set((currentState) => ({
      state: {
        ...currentState.state,
        listings: currentState.state.listings.map((listing) =>
          listing.listing_id === listingId
            ? {
                ...listing,
                listing_type: newType,
              }
            : listing,
        ),
      },
      settingsModalListing:
        currentState.settingsModalListing?.listing_id === listingId
          ? { ...currentState.settingsModalListing, listing_type: newType }
          : currentState.settingsModalListing,
    }));

    try {
      const response = await fetch(`${LISTINGSENDPOINTURL}/changeListingType`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionId,
          origin: "http://doomum.ro:3000/",
        },
        body: JSON.stringify({ listing_id: listingId, listing_type: newType }),
      });
      if (!response.ok) {
        const apiErr = (await response.json()) as ApiError;
        throw new Error(apiErr.message);
      }
      if (myListingsSyncChannel) {
        myListingsSyncChannel.postMessage({
          type: "UPDATED",
          listingId,
          partialData: { listing_type: newType },
        });
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      toast(
        errorMessage === "Failed to fetch"
          ? "Unable to update your listing type."
          : errorMessage,
        {
          type: "error",
        },
      );

      // Revert UI state on failure
      if (prevListing) {
        set((currentState) => ({
          state: {
            ...currentState.state,
            listings: currentState.state.listings.map((listing) =>
              listing.listing_id === listingId ? prevListing : listing,
            ),
          },
          settingsModalListing:
            currentState.settingsModalListing?.listing_id === listingId
              ? prevListing
              : currentState.settingsModalListing,
        }));
      }
      return;
    }
  },
  saveListingSettings: async (listingId, sessionId, changes) => {
    const { activateListing, deactivateListing, changePlan, cancelPlan } =
      get();
    if (!sessionId) {
      toast("Please reauthenticate before performing this action.", {
        type: "error",
      });
      return;
    }
    const tasks: Promise<boolean>[] = [];
    if (changes.newPlan) {
      tasks.push(changePlan(listingId, changes.newPlan, sessionId));
    }
    if (changes.cancelPlan) {
      tasks.push(cancelPlan(listingId, sessionId));
    }
    if (changes.status === "active") {
      tasks.push(activateListing(listingId, sessionId));
    } else if (changes.status === "inactive") {
      tasks.push(deactivateListing(listingId, sessionId));
    }

    if (tasks.length === 0) {
      return;
    }
    const results = await Promise.all(tasks);
    const allSucceeded = results.every((r) => r === true);

    if (allSucceeded) {
      toast.success("Listing settings updated successfully.");
      set({ settingsModalListing: null });
    } else {
      return;
      // toast.error("Some updates failed. Please review and retry.");
    }
  },
  deleteListing: async (locale, sessionId) => {
    if (!sessionId) {
      toast("Please reauthenticate before performing this action.", {
        type: "error",
      });
      return;
    }
    const deleteState = get().deleteState.listingState;
    if (!deleteState) {
      return;
    }
    const listingId = deleteState.listing_id;
    const prevListing = get().state.listings.find(
      (l) => l.listing_id === listingId,
    );
    try {
      const response = await fetch(
        `${LISTINGSENDPOINTURL}/deleteListingPreLaunch`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionId,
          },
          body: JSON.stringify({
            listing_id: deleteState.listing_id,
            sold_on_doomum: deleteState.sold_on_doomum,
          }),
        },
      );

      if (!response.ok) {
        const apiErr = (await response.json()) as ApiError;
        throw new Error(apiErr.message);
      }
      set((currentState) => ({
        ...currentState,
        state: {
          ...currentState.state,
          listings: currentState.state.listings.filter(
            (listing) => listing.listing_id !== listingId,
          ),
        },
        deleteState: {
          isDeleteModalOpen: false,
          isSecondStep: false,
          listingState: null,
        },
      }));
      if (myListingsSyncChannel) {
        myListingsSyncChannel.postMessage({ type: "DELETED", listingId });
      }
      const currentPage = get().state.page_number;
      const remainingOnPage = get().state.listings.length - 1;
      const newPage =
        remainingOnPage === 0 && currentPage > 1
          ? currentPage - 1
          : currentPage;
      setTimeout(() => {
        get().fetchPage(locale, newPage, sessionId);
      }, 750);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      toast(
        errorMessage === "Failed to fetch"
          ? "Unable to delete your listing."
          : errorMessage,
        {
          type: "error",
        },
      );
      if (prevListing) {
        set((currentState) => ({
          state: {
            ...currentState.state,
            listings: currentState.state.listings.map((listing) =>
              listing.listing_id === listingId ? prevListing : listing,
            ),
          },
        }));
      }
      return;
    }
  },
  getListingById: async (
    listingId: string,
    locale: string,
  ): Promise<UserListingCard | null> => {
    try {
      const response = await apiFetch(
        `/api/search/getUserListingById?listing_id=${listingId}&locale=${locale}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        const apiErr = (await response.json()) as ApiError;
        throw new Error(apiErr.message);
      }

      const data = (await response.json()) as GetListingByIdResponse;

      if (!data?.listing?.listing_id) {
        return null;
      }
      const listing = data.listing;
      set((state) => {
        const exists = state.state.listings.some(
          (l) => l.listing_id === listing.listing_id,
        );

        if (exists) {
          return state;
        }

        return {
          ...state,
          pinnedListing: listing,
          state: {
            ...state.state,
            listings: [listing, ...state.state.listings], // 👈 insert first
            total_count: state.state.total_count + 1,
          },
        };
      });

      return listing;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unexpected error occurred.";

      toast(
        errorMessage === "Failed to fetch"
          ? "Unable to get listing."
          : errorMessage,
        {
          type: "error",
        },
      );

      return null;
    }
  },
  openDeleteModal: (listingId) => {
    set({
      deleteState: {
        isDeleteModalOpen: true,
        isSecondStep: false,
        listingState: { listing_id: listingId, sold_on_doomum: false },
      },
    });
  },
  closeDeleteModal: () => {
    set({
      deleteState: {
        isDeleteModalOpen: false,
        isSecondStep: false,
        listingState: null,
      },
    });
  },
  setSoldOnDoomum: (value) => {
    const listing_id = get().deleteState.listingState?.listing_id;
    if (!listing_id) {
      return;
    }
    set((state) => ({
      deleteState: {
        ...state.deleteState,
        isSecondStep: true,
        listingState: {
          listing_id: listing_id,
          sold_on_doomum: value,
        },
      },
    }));
  },
  deleteState: {
    isSecondStep: false,
    isDeleteModalOpen: false,
    listingState: null,
  },
  refetchEditedListing: async (listingId, sessionId, locale) => {
    if (!sessionId) {
      toast("Please reauthenticate before performing this action.", {
        type: "error",
      });
      return;
    }
    set((state) => ({
      isRefetchingListingCardLoading: listingId,
    }));
    // await new Promise((resolve) => setTimeout(resolve, 1000));
    try {
      const response = await fetch(
        `${LISTINGSENDPOINTURL}/refetchEditedListingCard?locale=${locale}&listing_id=${listingId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionId,
          },
        },
      );
      if (response.ok) {
        const listingCard = (await response.json()) as UserListingCard;
        set((currentState) => ({
          state: {
            ...currentState.state,
            listings: currentState.state.listings.map((item) =>
              item.listing_id === listingCard.listing_id ? listingCard : item,
            ),
          },
          isRefetchingListingCardLoading: "",
        }));
        if (myListingsSyncChannel) {
          myListingsSyncChannel.postMessage({
            type: "UPDATED",
            listingId,
            partialData: listingCard,
          });
        }
      } else {
        const apiErr = (await response.json()) as ApiError;
        throw new Error(apiErr.message);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      toast(
        errorMessage === "Failed to fetch"
          ? "Unable to get updated listing."
          : errorMessage,
        {
          type: "error",
        },
      );
      set((state) => ({
        isRefetchingListingCardLoading: "",
      }));
      return;
    }
  },
  text: "",
  type: TypeEnum.DEFAULT,
  purpose: PurposeEnum.DEFAULT,
  onTextChange: (e, locale, sessionId) => {
    e.preventDefault();
    const text = e.target.value;
    set({ text: text });
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }
    debounceTimeout = setTimeout(() => {
      set((currentState) => ({
        ...currentState,
        state: {
          ...currentState.state,
          current_suggestion: {
            value: "",
            type: "",
            page_number: 1,
          },
        },
      }));
      get().refetchState(locale, sessionId);
    }, 300);
  },
  refetchState: async (locale, sessionId) => {
    if (!sessionId) {
      toast("Please reauthenticate before performing this action.", {
        type: "error",
      });
      return;
    }
    await get().fetchPage(locale, 1, sessionId);
    // await Promise.all([]);
  },
  setType: (type: TypeEnum) => {
    set({
      type: type,
    });
  },
  setPurpose: (purpose: PurposeEnum) => {
    set({
      purpose: purpose,
    });
  },
}));

if (myListingsSyncChannel) {
  myListingsSyncChannel.onmessage = (event: MessageEvent<SyncEvent>) => {
    // We cast it to any here just to access the method,
    // or you can export applySyncEvent in your store interface.
    useMyListingsStore.getState().applySyncEvent(event.data);
  };
}
